import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Mail, ClipboardList, Award, Pencil } from 'lucide-react';
import { useApi, useUserContext } from './DashboardLayout';
import { Link } from 'react-router-dom';
import MailLogPage from './MailLogPage';

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label className="of-label">{label}</label>
    {children}
  </div>
);

const TF = ({ label, field, value, onChange, type = 'text', required, disabled, placeholder }) => (
  <Field label={label}>
    <input className="of-input" type={type} value={value} onChange={e => onChange(field, e.target.value)}
      required={required} placeholder={placeholder}
      style={disabled ? { background: 'var(--page-bg)', cursor: 'not-allowed' } : {}}
      disabled={disabled} />
  </Field>
);

export default function ProviderForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { apiFetch } = useApi();
  const { userType } = useUserContext();
  const isAdmin = userType === 1 || userType === 2;
  const [searchParams] = useSearchParams();
  const backUrl = searchParams.get('back');
  const [form, setForm] = useState({
    ik_number: '', club_id: '', password: '', verein: '', street: '', postal_code: '',
    city_location: '', phone: '', contact_person: '', email: '', website: '',
    dachverband: '', billing_office_name: '', billing_office_ik_number: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    if (isEdit) {
      apiFetch(`/api/providers/${id}`).then(r => r?.json()).then(d => {
        if (d && !d.detail) setForm(prev => { const u = { ...prev }; Object.keys(u).forEach(k => { if (d[k] !== undefined && k !== 'password') u[k] = d[k] || ''; }); return u; });
      });
      apiFetch(`/api/offers?provider_id=${id}`).then(r => r?.json()).then(d => {
        if (Array.isArray(d)) setOffers(d);
      });
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      if (isEdit) {
        const { password, ...updateData } = form;
        if (!isAdmin) delete updateData.club_id;
        const r = await apiFetch(`/api/providers/${id}`, { method: 'PUT', body: JSON.stringify(updateData) });
        if (r?.ok) goBack(); else { const text = await r?.text(); try { const d = JSON.parse(text); setError(d?.detail || 'Fehler'); } catch { setError('Fehler beim Speichern'); } }
      } else {
        if (!form.password || form.password.length < 8) { setError('Passwort muss mindestens 8 Zeichen lang sein'); setLoading(false); return; }
        const r = await apiFetch('/api/providers', { method: 'POST', body: JSON.stringify(form) });
        if (r?.ok) navigate('/app/providers'); else { const text = await r?.text(); try { const d = JSON.parse(text); setError(d?.detail || 'Fehler'); } catch { setError('Fehler beim Speichern'); } }
      }
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const goBack = () => {
    if (backUrl) { navigate(backUrl); return; }
    if (isEdit) navigate(`/app/providers?view=${id}`);
    else navigate('/app/providers');
  };

  return (
    <div data-testid="provider-form-page">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={goBack} data-testid="provider-back-btn">
          <ArrowLeft size={14} /> Zurück
        </button>
        <h2 style={{ margin: 0, fontSize: 20 }}>{isEdit ? 'Anbieter bearbeiten' : 'Neuer Anbieter'}</h2>
      </div>

      {error && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 12, fontWeight: 500 }}>{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Two column layout: Vereinsdaten + Kontaktdaten */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          {/* LEFT: Vereinsdaten */}
          <div className="of-card">
            <div className="of-section">Vereinsdaten</div>
            <div className="of-2col">
              <TF label="IK-Nummer" field="ik_number" value={form.ik_number} onChange={set} required />
              <TF label="Club-ID" field="club_id" value={form.club_id} onChange={set} required={!isEdit} disabled={isEdit} />
            </div>
            <TF label="Verein" field="verein" value={form.verein} onChange={set} required />
            <TF label="Dachverband" field="dachverband" value={form.dachverband} onChange={set} />
            {!isEdit && <TF label="Passwort" field="password" type="password" value={form.password} onChange={set} required />}
          </div>

          {/* RIGHT: Kontaktdaten */}
          <div className="of-card">
            <div className="of-section">Kontaktdaten</div>
            <TF label="Ansprechpartner" field="contact_person" value={form.contact_person} onChange={set} required />
            <div className="of-2col">
              <TF label="E-Mail" field="email" type="email" value={form.email} onChange={set} required />
              <TF label="Telefon" field="phone" value={form.phone} onChange={set} required />
            </div>
            <TF label="Website" field="website" value={form.website} onChange={set} />
          </div>
        </div>

        {/* Full width: Adresse & Abrechnung */}
        <div className="of-card" style={{ marginBottom: 16 }}>
          <div className="of-section">Adresse & Abrechnung</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 16px' }}>
            <TF label="Straße" field="street" value={form.street} onChange={set} required />
            <TF label="PLZ" field="postal_code" value={form.postal_code} onChange={set} required />
            <TF label="Ort" field="city_location" value={form.city_location} onChange={set} required />
          </div>
          <div className="of-2col">
            <TF label="Abrechnungsstelle" field="billing_office_name" value={form.billing_office_name} onChange={set} />
            <TF label="Abrechnungsstelle IK" field="billing_office_ik_number" value={form.billing_office_ik_number} onChange={set} />
          </div>
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" type="submit" disabled={loading} data-testid="provider-submit-btn" style={{ padding: '8px 24px' }}>
            {loading ? 'Speichern...' : isEdit ? 'Aktualisieren' : 'Erstellen'}
          </button>
          <button className="btn btn-secondary" type="button" onClick={goBack} style={{ padding: '8px 20px' }}>Abbrechen</button>
        </div>
      </form>

      {/* Angebote dieses Anbieters */}
      {isEdit && (
        <div className="of-card" style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="of-section" style={{ marginBottom: 0 }}>Angebote ({offers.length})</div>
            <Link to={`/app/offers/new?provider_id=${id}`} style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }} data-testid="provider-add-offer">
              + Neues Angebot
            </Link>
          </div>
          {offers.length > 0 ? (
            <div className="table-wrap" style={{ margin: '0 -20px -20px', borderTop: 'none' }}>
              <table data-testid="provider-offers-table">
                <thead>
                  <tr>
                    <th>Nr.</th>
                    <th>Angebot</th>
                    <th>Veranstaltungsort</th>
                    <th>Status</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {offers.map(o => (
                    <tr key={o.id} data-testid={`provider-offer-${o.id}`}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>{o.id}</td>
                      <td style={{ fontWeight: 500 }}>{o.angebot || o.offer_name || '-'}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{o.veranstaltungsort || '-'}</td>
                      <td>
                        <span className={`badge ${o.certified === 1 ? 'badge-success' : 'badge-warning'}`}>
                          {o.certified === 1 ? <><Award size={11} /> Zertifiziert</> : 'Nicht zertifiziert'}
                        </span>
                      </td>
                      <td>
                        <Link to={`/app/offers/${o.id}/edit`} className="btn-icon edit" title="Bearbeiten" data-testid={`provider-offer-edit-${o.id}`}>
                          <Pencil size={14} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
              <ClipboardList size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p style={{ margin: 0, fontSize: 13 }}>Keine Angebote vorhanden</p>
            </div>
          )}
        </div>
      )}

      {/* Mail-Historie */}
      {isEdit && (
        <div className="of-card" style={{ marginTop: 24 }}>
          <div className="of-section" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Mail size={14} /> E-Mail-Verlauf
          </div>
          <div style={{ margin: '0 -20px -20px' }}>
            <MailLogPage providerId={parseInt(id)} />
          </div>
        </div>
      )}
    </div>
  );
}
